import { createContext, useContext, useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { posthog, posthogLoaded } from "@/lib/posthog";

import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    // The trigger normally creates the row at signup, but be defensive.
    console.error("[auth] failed to load profile:", error);
    return null;
  }
  return data ?? null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (nextSession?.user) {
        // Defer the profile fetch so Supabase's internal state machine
        // finishes before we trigger another query. Recommended by the
        // Supabase docs for auth listeners.
        setTimeout(async () => {
          const p = await fetchProfile(nextSession.user.id);
          setProfile(p);
        }, 0);

        if (posthogLoaded()) {
          posthog().identify(nextSession.user.id, {
            email: nextSession.user.email,
          });
        }
      } else {
        setProfile(null);
        if (event === "SIGNED_OUT" && posthogLoaded()) {
          posthog().reset();
        }
      }

      setLoading(false);
    });

    // Hydrate initial state.
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        const p = await fetchProfile(s.user.id);
        setProfile(p);
        if (posthogLoaded()) {
          posthog().identify(s.user.id, { email: s.user.email });
        }
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sin OAuth: el alta está cerrada y un signInWithOAuth crea la cuenta sí o sí
  // (no admite shouldCreateUser:false), así que dejaría la puerta abierta a
  // cualquiera. El único acceso es el magic link con shouldCreateUser:false
  // desde /login, que rechaza en servidor a quien no sea socio.

  const signOut = async () => {
    // Scope 'local' clears the current browser only; other sessions of the
    // same user stay valid. Safer than wiping localStorage wholesale (would
    // also drop the cookie consent for PostHog).
    await supabase.auth.signOut({ scope: "local" });
    // Give the listener a tick to propagate SIGNED_OUT → posthog().reset().
    setTimeout(() => {
      window.location.href = "/";
    }, 50);
  };

  const value: AuthContextValue = {
    user,
    session,
    profile,
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
